import React, { useEffect, useContext, memo } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useDrag } from 'react-dnd';
import get from 'lodash.get';
import isEqual from 'lodash.isequal';

import { AppState } from 'services/types';
import { VirtualizedTableColumnKey } from 'services/virtualizedTable/types';
import {
    setColumnWidth as setColumnWidthAction,
    setColumnWidthOffset as setColumnWidthOffsetAction,
} from 'services/virtualizedTable/actions';
import {
    selectVirtualizedTableColumnWidthAdjusted,
    makeSelectVirtualizedTableColumnWidthAdjusted,
} from 'services/virtualizedTable/selectors';

import VirtualizedTableContext from '../VirtualizedTableContext';
import { VirtualizedTableContextProviderValue } from '../../types';
import { VIRTUALIZED_TABLE_HEADER_COLUMN_RESIZE_HANDLE } from './constants';
import './styles.scss';

interface OwnProps {
    columnKey: VirtualizedTableColumnKey;
    minWidth: number;
}

export interface ConnectedProps {
    columnWidth?: ReturnType<typeof selectVirtualizedTableColumnWidthAdjusted>;
}

export interface DispatchProps {
    setColumnWidth: typeof setColumnWidthAction;
    setColumnWidthOffset: typeof setColumnWidthOffsetAction;
}

export type Props = OwnProps & ConnectedProps & DispatchProps;

export const VirtualizedTableHeaderColumnResizeHandleComponent = (
    props: Props,
): React.ReactElement => {
    const { columnKey, minWidth, columnWidth, setColumnWidth, setColumnWidthOffset } = props;
    let newMinWidth = 0;
    if (minWidth > 0 && newMinWidth == 0) {
        newMinWidth = minWidth + 46;
    }

    const { onColumnResized }: VirtualizedTableContextProviderValue =
        useContext(VirtualizedTableContext);

    const [{ isDragging, differenceFromInitialOffset }, drag] = useDrag({
        type: VIRTUALIZED_TABLE_HEADER_COLUMN_RESIZE_HANDLE,
        item: {
            id: columnKey,
            type: VIRTUALIZED_TABLE_HEADER_COLUMN_RESIZE_HANDLE,
        },
        end: (_item, monitor) => {
            const newColumnWidth = columnWidth && (columnWidth >= newMinWidth) ? columnWidth : newMinWidth;
            setColumnWidth(columnKey, newColumnWidth);

            if (onColumnResized) {
                onColumnResized(columnKey, newColumnWidth);
            }
        },
        collect: (monitor) => {
            return {
                isDragging: (columnWidth && (columnWidth >= newMinWidth)) ? monitor.isDragging() : false,
                differenceFromInitialOffset:
                    monitor.getDifferenceFromInitialOffset(),
            };
        },
    });

    const offset = get(differenceFromInitialOffset, 'x') ?? 0;

    useEffect(() => {
        if (columnWidth) {
            if (!(columnWidth <= newMinWidth && offset < 0) || (columnWidth >= newMinWidth && offset > 0)) {
                if ((isDragging)) {
                    setColumnWidthOffset(columnKey, offset);
                }
            } else {
                setColumnWidth(columnKey, newMinWidth);
            }
        }
    }, [columnKey, isDragging, offset, setColumnWidthOffset]);

    return (
        <div
            ref={drag}
            className="virtualized-table-header-column-resize-handle"
        >
            <div className="virtualized-table-header-column-dragging-line" />
        </div>
    );
};

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    columnWidth: makeSelectVirtualizedTableColumnWidthAdjusted(),
});

export default connect(mapStateToProps, {
    setColumnWidth: setColumnWidthAction,
    setColumnWidthOffset: setColumnWidthOffsetAction,
})(memo(VirtualizedTableHeaderColumnResizeHandleComponent, isEqual));
